import { useEffect, useRef, useState } from 'react';
import './DicomViewer.css';
import cornerstone from 'cornerstone-core';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dicomParser from 'dicom-parser';
import DicomTags from './DicomTags';
import WindowAdjustment from './WindowAdjustment';
import ImageNavigationControls from './ImageNavigationControls';
import CornerstoneViewport from './CornerstoneViewport';
import FileUploader from './FileUploader';

const DicomViewer = () => {
  const viewportRef = useRef(null);
  const [imageIds, setImageIds] = useState([]);
  const [index, setIndex] = useState(0);

  const imageIdsRef = useRef([]);
  const indexRef = useRef(0);
  const wheelTimeoutRef = useRef(null);
  const [windowCenter, setWindowCenter] = useState(40);
  const [windowWidth, setWindowWidth] = useState(400);
  const [dicomMetadata, setDicomMetadata] = useState(null);
  const [windowPresets, setWindowPresets] = useState([]);

  useEffect(() => {
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
    cornerstoneWADOImageLoader.configure({ useWebWorkers: false });

    return () => {
      imageIdsRef.current.forEach(id => {
        if (typeof id === 'string' && id.startsWith('wadouri:blob:')) {
          const blobUrl = id.replace(/^wadouri:/, '');
          try { URL.revokeObjectURL(blobUrl); } catch (e) {}
        }
      });
    };
  }, []);

  const enableViewport = () => {
    const el = viewportRef.current;
    if (!el) return null;
    try { cornerstone.enable(el); } catch (e) {}
    return el;
  };

  const loadImageAt = (i) => {
    const ids = imageIdsRef.current || imageIds;
    if (!ids || ids.length === 0) return;
    if (i < 0 || i >= ids.length) return;
    const id = ids[i];
    const el = enableViewport();
    if (!el) return;

    cornerstone.loadAndCacheImage(id).then(image => {
      try {
        cornerstone.displayImage(el, image);
        cornerstone.resize(el);
        cornerstone.fitToWindow(el);

        const viewport = cornerstone.getViewport(el);
        const wc = Math.round(viewport.voi.windowCenter);
        const ww = Math.round(viewport.voi.windowWidth);
        setWindowCenter(wc);
        setWindowWidth(ww);

        if (image.data) {
          const metadata = {
            rows: image.rows,
            columns: image.columns,
            windowCenter: wc,
            windowWidth: ww,
            rescaleSlope: image.slope || 1,
            rescaleIntercept: image.intercept || 0
          };
          setDicomMetadata(metadata);

          try {
            const dataSet = image.data;
            if (dataSet && dataSet.string) {
              const wcStr = dataSet.string('x00281050');
              const wwStr = dataSet.string('x00281051');
              const descStr = dataSet.string('x00281055');

              if (wcStr && wwStr) {
                const centers = wcStr.split('\\').map(v => parseFloat(v));
                const widths = wwStr.split('\\').map(v => parseFloat(v));
                const descriptions = descStr ? descStr.split('\\') : [];

                const presets = [];
                for (let p = 0; p < Math.min(centers.length, widths.length); p++) {
                  presets.push({
                    name: descriptions[p] || `Preset ${p + 1}`,
                    center: centers[p],
                    width: widths[p]
                  });
                }
                setWindowPresets(presets);
              }
            }
          } catch { }
        }
      } catch { }
    }).catch(() => { });
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files || files.length === 0) return;

    imageIdsRef.current.forEach(id => {
      if (typeof id === 'string' && id.startsWith('wadouri:blob:')) {
        const blobUrl = id.replace(/^wadouri:/, '');
        try { URL.revokeObjectURL(blobUrl); } catch (e) {}
      }
    });

    const ids = files.map(f => {
      const blobUrl = URL.createObjectURL(f);
      return 'wadouri:' + blobUrl;
    });

    setImageIds(ids);
    imageIdsRef.current = ids;
    setIndex(0);
    indexRef.current = 0;
    setTimeout(() => loadImageAt(0), 50);
  };

  const onPrev = () => {
    const ids = imageIdsRef.current || imageIds;
    if (!ids || ids.length === 0) return;
    const ni = Math.max(0, indexRef.current - 1);
    indexRef.current = ni;
    setIndex(ni);
    loadImageAt(ni);
  };

  const onNext = () => {
    const ids = imageIdsRef.current || imageIds;
    if (!ids || ids.length === 0) return;
    const ni = Math.min(ids.length - 1, indexRef.current + 1);
    indexRef.current = ni;
    setIndex(ni);
    loadImageAt(ni);
  };

  const handleWheel = (e) => {
    const ids = imageIdsRef.current || imageIds;
    if (!ids || ids.length === 0) return;
    if (wheelTimeoutRef.current) return;
    const delta = e.deltaY;
    let ni = indexRef.current + (delta > 0 ? 1 : -1);
    ni = Math.max(0, Math.min(ids.length - 1, ni));
    if (ni === indexRef.current) return;
    indexRef.current = ni;
    setIndex(ni);
    loadImageAt(ni);
    wheelTimeoutRef.current = setTimeout(() => { wheelTimeoutRef.current = null; }, 80);
  };

  const onWindowCenterChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setWindowCenter(value);
    const el = viewportRef.current;
    if (el && imageIds.length > 0) {
      try {
        const viewport = cornerstone.getViewport(el);
        viewport.voi.windowCenter = value;
        cornerstone.setViewport(el, viewport);
      } catch (e) {}
    }
  };

  const onWindowWidthChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setWindowWidth(value);
    const el = viewportRef.current;
    if (el && imageIds.length > 0) {
      try {
        const viewport = cornerstone.getViewport(el);
        viewport.voi.windowWidth = value;
        cornerstone.setViewport(el, viewport);
      } catch (e) {}
    }
  };

  const resetWindowLevel = () => {
    const el = viewportRef.current;
    if (el && imageIds.length > 0) {
      try {
        cornerstone.reset(el);
        const newViewport = cornerstone.getViewport(el);
        setWindowCenter(Math.round(newViewport.voi.windowCenter));
        setWindowWidth(Math.round(newViewport.voi.windowWidth));
      } catch (e) {}
    }
  };

  const applyPreset = (preset) => {
    const el = viewportRef.current;
    if (el && imageIds.length > 0) {
      try {
        const viewport = cornerstone.getViewport(el);
        viewport.voi.windowCenter = preset.center;
        viewport.voi.windowWidth = preset.width;
        cornerstone.setViewport(el, viewport);
        setWindowCenter(Math.round(preset.center));
        setWindowWidth(Math.round(preset.width));
      } catch (e) {}
    }
  };

  return (
    <div className="dicom-viewer">
      <h2>Upload DICOM Files</h2>
      <p>Select DICOM files (drag or browse).</p>
      <br/>
      <FileUploader onFilesSelected={onFiles} />
      <br/>
      <ImageNavigationControls
        index={index}
        totalImages={imageIds.length}
        onPrev={onPrev}
        onNext={onNext}
      />

      <WindowAdjustment
        windowCenter={windowCenter}
        windowWidth={windowWidth}
        onWindowCenterChange={onWindowCenterChange}
        onWindowWidthChange={onWindowWidthChange}
        onReset={resetWindowLevel}
        windowPresets={windowPresets}
        onApplyPreset={applyPreset}
        disabled={imageIds.length === 0}
      />

      <CornerstoneViewport
        ref={viewportRef}
        width="100%"
        height="512px"
        onWheel={handleWheel}
      />

      <DicomTags
        metadata={dicomMetadata}
        windowCenter={windowCenter}
        windowWidth={windowWidth}
        windowPresets={windowPresets}
      />
    </div>
  );
};

export default DicomViewer;
